import { useCallback, useEffect, useState } from "react";
import { ROWS } from "../components/jungle/JSchedule";
import { GENETICS, planColumns } from "./plan";

// One grow config shared by "Mi cultivo", the Riego section and the hero icon (saved per browser):
// start date, genetics + durations, and watering days (fixed weekdays or null = alternate with Sunday rest).
const KEY = "jungle-grow";
const EVT = "jungle-grow";

export const toInput = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const isDate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s ?? "");

export const parseDays = (s) => {
  const v = [...new Set(String(s ?? "").split(",").filter((x) => /^[0-6]$/.test(x)).map(Number))];
  return v.length ? v : null;
};

const saved = () => {
  try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch { return {}; }
};

export const readGrow = () => {
  const p = new URLSearchParams(window.location.search);
  const s = saved();
  const genetic = GENETICS[p.get("gen")] ? p.get("gen") : GENETICS[s.genetic] ? s.genetic : "hibrida";
  const g = GENETICS[genetic];
  const flower = Number(p.get("flor")) >= 7 && Number(p.get("flor")) <= 12 ? Number(p.get("flor"))
    : s.genetic === genetic && s.flower >= 7 && s.flower <= 12 ? s.flower : g.flower;
  const start = isDate(p.get("inicio")) ? p.get("inicio") : isDate(s.start) ? s.start : toInput(new Date());
  let days;
  if (p.get("dias") !== null) days = parseDays(p.get("dias"));
  else if (p.has("inicio")) days = null; // QR without fixed days = alternate
  else days = Array.isArray(s.days) ? parseDays(s.days.join(",")) : s.days === null ? null : [1, 3, 5]; // official sheet: L · X · V
  return { start, genetic, growth: g.growth, flower, days };
};

const save = (cfg) => {
  try { localStorage.setItem(KEY, JSON.stringify(cfg)); } catch { /* storage blocked */ }
  window.dispatchEvent(new CustomEvent(EVT, { detail: cfg }));
};

// [cfg, update(patch)] kept in sync across every component that uses it.
export const useGrow = () => {
  const [cfg, setCfg] = useState(readGrow);
  useEffect(() => {
    const on = (e) => setCfg(e.detail);
    window.addEventListener(EVT, on);
    return () => window.removeEventListener(EVT, on);
  }, []);
  const update = useCallback((patch) => setCfg((c) => {
    const next = { ...c, ...patch };
    setTimeout(() => save(next), 0);
    return next;
  }), []);
  return [cfg, update];
};

export const growQuery = ({ start, days, genetic, flower }) =>
  `?inicio=${start}&gen=${genetic}&flor=${flower}${days ? `&dias=${[...days].sort().join(",")}` : ""}`;

export const dosesFor = (col) =>
  ROWS.filter(([, , , doses]) => doses?.[col]).map(([id, name, , doses]) => ({ id, name, dose: doses[col] }));

// Weeks of the plan with each day: water or rest + doses (F8 = lavado, water only).
export const buildWeeks = ({ start, days, growth, flower }) => {
  const [y, m, d] = start.split("-").map(Number);
  const s = new Date(y, (m || 1) - 1, d || 1);
  const cols = planColumns(growth, flower);
  let k = 0;
  return cols.map((col, w) => {
    const list = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(s.getFullYear(), s.getMonth(), s.getDate() + w * 7 + i);
      const sunday = date.getDay() === 0;
      const water = days ? days.includes(date.getDay()) : !sunday && k++ % 2 === 0;
      return { date, water, sunday, doses: water ? dosesFor(col) : [] };
    });
    const label = w < growth ? `Crecimiento S${w + 1}` : col === "F8" ? "Lavado" : `Floración S${w - growth + 1}`;
    return { n: w + 1, col, label, days: list };
  });
};

// Consecutive planned waterings done, counting back from today (today only counts once ticked).
export const computeStreak = (cfg, doneDates) => {
  const t = toInput(new Date());
  const planned = buildWeeks(cfg).flatMap((w) => w.days).filter((d) => d.water).map((d) => toInput(d.date)).filter((k) => k <= t).reverse();
  let n = 0;
  for (const k of planned) {
    if (doneDates.has(k)) n += 1;
    else if (k === t) continue;
    else break;
  }
  return n;
};

// Today's entry (+ next watering day); null when today is outside the plan.
export const todayIrrigation = (cfg) => {
  const t = toInput(new Date());
  const flat = buildWeeks(cfg).flatMap((w) => w.days.map((x) => ({ ...x, col: w.col, label: w.label, n: w.n })));
  const i = flat.findIndex((x) => toInput(x.date) === t);
  if (i < 0) return null;
  return { ...flat[i], next: flat.slice(i + 1).find((x) => x.water) ?? null };
};
